import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface Quadrant {
  name: string;
  icon: string;
  label: string;
  tasks: string[];
  color: string;
  action: string;
}

const QUADRANTS: Quadrant[] = [
  {
    name: '重要且紧急',
    icon: '🔥',
    label: 'Q1',
    tasks: ['紧急截止任务', '突发危机处理', '客户投诉'],
    color: '#FF4757',
    action: '立即执行',
  },
  {
    name: '重要不紧急',
    icon: '🎯',
    label: 'Q2',
    tasks: ['长期规划', '技能学习', '健康锻炼'],
    color: '#2ED573',
    action: '专注投入',
  },
  {
    name: '紧急不重要',
    icon: '📞',
    label: 'Q3',
    tasks: ['无关会议', '他人请求', '部分邮件'],
    color: '#FFD700',
    action: '委托他人',
  },
  {
    name: '不重要不紧急',
    icon: '📱',
    label: 'Q4',
    tasks: ['无目的刷手机', '闲聊八卦', '过度追剧'],
    color: '#A4B0BE',
    action: '尽量减少',
  },
];

const TECHNIQUES = [
  { name: '番茄工作法', desc: '25min专注 + 5min休息', icon: '🍅', color: '#FF6B35' },
  { name: '两分钟法则', desc: '能在2分钟内完成则立刻做', icon: '⏱️', color: '#4ECDC4' },
  { name: '时间块法', desc: '将一天切分为专注时间块', icon: '📊', color: '#667EEA' },
  { name: '3-3-3法则', desc: '3h深度工作+3件短任务+3件杂事', icon: '🎲', color: '#9B59B6' },
];

export const TimeManagementAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  return (
    <AbsoluteFill
      style={{
        background: '#0B0E17',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 60,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF4757, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          艾森豪威尔矩阵
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Eisenhower Matrix · 四象限时间管理法
        </p>
      </div>

      {/* Matrix area */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: 55,
          right: 55,
          bottom: 120,
        }}
      >
        {/* 紧急程度 — horizontal label above the grid */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 46,
            right: 0,
            textAlign: 'center',
            fontSize: 24,
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 600,
            zIndex: 2,
            lineHeight: '28px',
          }}
        >
          ← 紧急程度 →
        </div>

        {/* 重要程度 — vertical label left of the grid */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 42,
            bottom: 0,
            width: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <span
            style={{
              transform: 'rotate(-90deg)',
              whiteSpace: 'nowrap',
              fontSize: 24,
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 600,
            }}
          >
            ← 重要程度 →
          </span>
        </div>

        {/* Grid */}
        <div
          style={{
            position: 'absolute',
            top: 42,
            left: 46,
            right: 0,
            bottom: 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: 16,
          }}
        >
          {QUADRANTS.map((q, i) => {
            const qProg = spring({
              frame: frame - 25 - i * 15,
              fps,
              config: { damping: 12, stiffness: 120 },
            });

            const taskRevealBase = 50 + i * 25;

            return (
              <div
                key={q.label}
                style={{
                  padding: '24px 28px',
                  background: `${q.color}08`,
                  border: `2px solid ${q.color}30`,
                  borderRadius: 20,
                  opacity: qProg,
                  transform: `scale(${qProg})`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <span
                    style={{
                      padding: '4px 12px',
                      background: `${q.color}25`,
                      borderRadius: 8,
                      fontSize: 30,
                      fontWeight: 800,
                      color: q.color,
                    }}
                  >
                    {q.label}
                  </span>
                  <span style={{ fontSize: 36 }}>{q.icon}</span>
                  <span style={{ fontSize: 30, fontWeight: 700, color: q.color }}>{q.name}</span>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {q.tasks.map((task, ti) => {
                    const taskProg = interpolate(
                      frame,
                      [taskRevealBase + ti * 10, taskRevealBase + ti * 10 + 15],
                      [0, 1],
                      { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                    );

                    return (
                      <div
                        key={ti}
                        style={{
                          padding: '8px 14px',
                          background: 'rgba(255,255,255,0.04)',
                          borderRadius: 10,
                          fontSize: 28,
                          color: '#D1D5DB',
                          opacity: taskProg,
                          transform: `translateX(${interpolate(taskProg, [0, 1], [20, 0])}px)`,
                        }}
                      >
                        • {task}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: '8px 16px',
                    background: `${q.color}15`,
                    borderRadius: 10,
                    fontSize: 28,
                    fontWeight: 700,
                    color: q.color,
                    textAlign: 'center',
                  }}
                >
                  → {q.action}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom techniques */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 14,
        }}
      >
        {TECHNIQUES.map((t, i) => {
          const tProg = spring({
            frame: frame - 200 - i * 12,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={t.name}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: `${t.color}08`,
                border: `1px solid ${t.color}25`,
                borderRadius: 12,
                opacity: tProg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 24 }}>{t.icon}</span>
              <div>
                <div style={{ fontSize: 24, fontWeight: 600, color: t.color }}>{t.name}</div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{t.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
